import { WorkflowCanvas } from "./workflowBuilderData";
import { PifLookupFlow } from "./pifWorkflowKeys";

/** A stage's key for matching bespoke screens (PIF_STAGE_KEYS): its stageKey, or - when the server
 *  returned none ("") - its id. */
export const stageKeyOf = (stage: WorkflowCanvas): string => stage.stageKey || stage.id;

/** Parse/format calendar days the same way PostIF does — local Date, never `new Date("YYYY-MM-DD")`,
 *  which is UTC and shifts the day in US timezones. The DatePicker value and the stored string
 *  must agree. */
export const parseWorkflowDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

export const formatWorkflowDate = (date: Date | null): string => {
  if (!date || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** The identity lookup's result, stored as `pif.lookupFlow` (see pifWorkflowKeys). */
export type PifIdentityFlow = PifLookupFlow;

export interface PifIdentityMatch {
  flow: PifIdentityFlow;
  name: string;
  employeeId: string;
  dateOfBirth: string;
  department?: string;
  phone?: string;
  email?: string;
  /** The name in parts, which pif-3 copies (existing employee) or carries over (rehire). */
  firstName?: string;
  middleName?: string;
  lastName?: string;
  /** A rehire's old contact details, which pif-3 pre-fills; an active employee's never come back. */
  address1?: string;
  city?: string;
  state?: string;
  zip?: string;
  homePhone?: string;
  cell?: string;
}

/** A real identity lookup's outcome. The PostIF lookup runs on PostIF's own access check, not the
 *  workflow permissions: `forbidden` is a 403 (no PostIF access), `rate-limited` a 429 (past the
 *  per-user lookup limit), and `failed` anything else. */
export type PifIdentityLookupOutcome =
  | { kind: "matched"; match: PifIdentityMatch }
  | { kind: "forbidden" }
  | { kind: "rate-limited" }
  | { kind: "failed" };

export type PifIdentityLookupError = Exclude<PifIdentityLookupOutcome, { kind: "matched" }>["kind"];

/** Each failed identity lookup explains itself in place — on pif-1, and on a reopened case's
 *  re-check by Employee ID (P3). Wording confirmed by Pooja on 80430 (2026-10-07). A 403 is not
 *  retryable, so the screens offer no retry for it. */
export const PIF_LOOKUP_ERROR_MESSAGES: Record<PifIdentityLookupError, string> = {
  failed: "Couldn't reach the employee directory. Try again.",
  forbidden: "You don't have access to the employee directory. Ask HR to grant you PostIF access.",
  "rate-limited": "Too many lookups. Please wait a minute and try again.",
};

export interface PifIdentityRoute {
  flow: PifIdentityFlow;
  label: string;
}

/** Keep these values aligned with the identity lookup flows above: step 2 evaluates the exact
 * result produced by step 1, and the real engine will use these same transition values. */
export const PIF_IDENTITY_ROUTES: PifIdentityRoute[] = [
  { flow: "existing-active", label: "Existing · Active" },
  { flow: "rehire", label: "Rehire · Inactive" },
  { flow: "new", label: "New applicant" },
  { flow: "blocked", label: "Do-Not-Rehire" },
];


export const getInitials = (name: string): string =>
  name
    .split(" ")
    .map((word) => word[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
